<template>
    <div class="print-preview print-overlay">
        <el-dialog :visible.sync="dialogVisible" :show-close="false" top="64px" :append-to-body="true" custom-class="print-overlay__dialog">
            <div class="print-overlay__content">
                <div class="print-overlay__head no-print">
                    <span class="bold-label">Druckvorschau</span>
                    <div class="print-overlay__close" @click="closeClickHandler">
                        <icon><IconClose/></icon>
                    </div>
                </div>
                <div class="print-preview__main">
                    <div class="print-preview__section print-preview__section--head ui-spread-center-vertically">
                        <div class="print-preview__icon-title ui-center-vertically">
                            <div class="print-preview__icon-title__icon print-preview__icon-title__icon--yellow">
                                <icon><IconOEAMTC /></icon>
                            </div>
                            <span class="h1">Ihre ÖAMTC Routenempfehlung</span>
                        </div>
                        <SmrButton custom-class="print-preview__button no-print" @click.native="printClickHandler" size="medium">Drucken</SmrButton>
                    </div>
                    <div v-for="(section, index) in availableSections" :key="index" :class="{'print-break-avoid' : section.type === 'map'}">
                        <div class="print-preview__section print-preview__section--highlight ui-spread-center-vertically print-break-avoid" :class="{ 'no-print' : !section.visible }" v-if="section.sectionTitle">
                            <div class="h1">{{ section.sectionTitle }}</div>
                            <div class="no-print" v-if="section.toggleable">
                                <SmrToggle :value="section.visible" @change="sectionVisibilityChangeHandler($event, index)" />
                            </div>
                        </div>
                        <div class="print-preview__section">
                            <div class="print-preview__icon-title ui-center-vertically" v-if="section.title">
                                <div class="print-preview__icon-title__icon">
                                    <icon>
                                        <TransportModeIcon :mode="formattedRoute.mode"/>
                                    </icon>
                                </div>
                                <span class="h1">{{ section.title }}</span>
                            </div>
                            <template v-if="section.visible">
                                <!-- summary -->
                                <div v-if="section.type === 'summary'">
                                    <div class="print-preview__waypoints">
                                        <WaypointSummary :waypoints="routeWaypoints" :compact="true" :compactToggleable="true" :showInterimAsNumbers="true" :distancesBetweenWaypoints="routeDetail?.interimDistances" :openLabel="'(Ausklappen zum Drucken)'" :closeLabel="'(Zuklappen, um nicht zu Drucken)'" />
                                    </div>
                                    <div class="print-preview__summary">
                                        <div class="print-preview__summary__item">
                                            <div class="copy-light">Datum</div>
                                            <div class="copy">{{ formattedRoute.date }}</div>
                                        </div>
                                        <div class="print-preview__summary__item" v-if="formattedRoute.time">
                                            <div class="copy-light">Uhrzeit</div>
                                            <div class="copy">{{ formattedRoute.time }}</div>
                                        </div>
                                        <div class="print-preview__summary__item" v-if="formattedRoute.departureTime">
                                            <div class="copy-light">Abfahrt</div>
                                            <div class="copy">{{ formattedRoute.departureTime }}</div>
                                        </div>
                                        <div class="print-preview__summary__item">
                                            <div class="copy-light">Dauer</div>
                                            <div class="copy">{{ formattedRoute.duration }}</div>
                                        </div>
                                        <div class="print-preview__summary__item" v-if="formattedRoute.transitions">
                                            <div class="copy-light">Umstiege</div>
                                            <div class="copy">{{ formattedRoute.transitions }}</div>
                                        </div>
                                        <div class="print-preview__summary__item" v-if="formattedRoute.distance">
                                            <div class="copy-light">Entfernung</div>
                                            <div class="copy">{{ formattedRoute.distance }}</div>
                                        </div>
                                        <div class="print-preview__summary__item print-preview__summary__item--wide" v-if="formattedRoute.costs">
                                            <div class="copy-light">Kosten</div>
                                            <div class="copy">
                                                <span v-for="cost in formattedRoute.costs" :key="cost" class="dot-divider dot-divider--info">{{ cost }}</span>
                                            </div>
                                        </div>
                                        <div class="print-preview__summary__item print-preview__summary__item--wide">
                                            <div class="copy-light">Optionen</div>
                                            <div class="copy">{{ formattedRoute.options }}</div>
                                        </div>
                                    </div>
                                    <div v-if="routeDetail?.routingHints && routeDetail.routingHints.length" class="print-preview__routing-hints">
                                        <div v-for="(hint, index) in routeDetail.routingHints" :key="index" class="print-preview__routing-hints__item ui-spread-center-vertically">
                                            <div class="print-preview__routing-hints__item__content">
                                                <div class="copy-light">{{ hint.title }}</div>
                                                <div class="copy" v-html="hint.text"></div>
                                            </div>
                                            <Icon class="red">
                                                <IconAlert />
                                            </Icon>
                                        </div>
                                    </div>
                                    <div class="print-preview__note">
                                        <SmrInput
                                            class="no-print"
                                            :value="note"
                                            inputType="textarea"
                                            placeholder="Persönliche Notiz"
                                            @change="noteChangeHandler($event)">
                                        </SmrInput>

                                        <div class="print-only" v-if="note !== ''">
                                            <div class="copy-light">Persönliche Notiz</div>
                                            <div class="copy">{{ note }}</div>
                                        </div>
                                    </div>
                                </div>
                                <!-- steps -->
                                <div v-if="section.type === 'steps'">
                                    <PublicRouteOverview :route="publicRouteData" v-if="publicRouteData" :printStyle="true" />
                                    <RouteSteps v-else :printStyle="true" :route="routeDetail" :steps="formattedRoute.steps" />
                                </div>
                                <!-- countryInfo -->
                                <div v-if="section.type === 'countryInfo'">
                                    <div class="print-preview__country-info" v-for="country in formattedRoute.countryInfo" :key="country.countryCode">
                                        <div class="print-preview__country-info__title ui-center-vertically">
                                            <img :src="country.image"/> 
                                            <span class="h1">{{ country.name }}</span>
                                        </div>
                                        <div class="print-preview__country-info__entry print-break-avoid" v-for="(entry, index) in country.data" :key="index">
                                            <div class="print-preview__country-info__entry__title h2">{{ entry.title }}</div>
                                            <div class="print-preview__country-info__entry__section" v-for="section in entry.content" :key="section.title">
                                                <div v-if="section.label" class="print-preview__country-info__entry__section__label h5" v-html="section.label"></div>
                                                <div v-if="section.subContent">
                                                    <div v-for="(subContent, index) in section.subContent" :key="index">
                                                        <div class="print-preview__country-info__entry__section__label h5" :class="{ 'print-preview__country-info__entry__section__label--space-top' : index > 0}">
                                                            {{ subContent.label }}
                                                        </div>
                                                        <div class="print-preview__country-info__entry__section__text copy" v-html="subContent.text"></div>
                                                    </div>
                                                </div>
                                                <div v-if="section.text" class="print-preview__country-info__entry__section__text copy" :class="{ 'print-preview__country-info__entry__section__text--space-top' : section.subContent }" v-html="section.text"></div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <!-- map -->
                                <div v-if="section.type === 'map'" class="print-break-avoid">
                                    <img class="print-preview__map" v-if="formattedRoute.map" :src="formattedRoute.map" alt="Routenkarte">
                                </div>
                                <!-- poi -->
                                <div v-if="section.type === 'pois'">
                                    <POIList
                                        v-if="poisLoaded || poiLoading"
                                        :pois="routePois"
                                        :active="true" 
                                        :loading="poiLoading"
                                        :more-loading="false"
                                        :current-sorting="currentSorting"
                                        :no-results-icon="false"
                                        :mapConnectionDisabled="true"
                                        :printStyle="true"
                                    />
                                </div>
                                <!-- hint -->
                                <div v-if="section.type === 'hint'">
                                    <div class="copy">
                                        Alle Angaben ohne Gewähr.
                                        <br>
                                        <br>
                                        Die Route ist eine Planungshilfe und basiert auf den zum Planungszeitpunkt bekannten Verkehrsbehinderungen. Die tatsächliche Fahrtdauer kann aufgrund von Verkehrsereignissen, Wetter, etc. abweichen. 
                                        <br>
                                        <br>
                                        Speichern Sie Ihre Route und rufen Sie sie jederzeit unter <a href="https://www.oeamtc.at/routenplaner">www.oeamtc.at/routenplaner</a> oder direkt am Handy in der ÖAMTC App wieder auf. So können Sie sich direkt vor und während der Fahrt über Störungen auf Ihrer Route informieren und schützen gleichzeitig unsere Umwelt.
                                    </div>
                                    <!-- <div class="print-preview__section__visual">
                                        <img :src="`${publicPath}/images/info/travel-info-kit.png`" alt="ÖAMTC Reise-Infoset">
                                    </div> -->
                                </div>
                            </template>
                        </div>
                    </div>
                </div>
            </div>
        </el-dialog>
    </div>
</template>

<script>
import { mapGetters } from 'vuex'
import * as api from '@/api';
import eventBus from '@/utils/event-bus';
import SmrButton from '@/components/SmrButton.vue'
import WaypointSummary from '@/components/WaypointSummary.vue'
import RouteSteps from '@/components/RouteSteps.vue'
import SmrToggle from '@/components/form/SmrToggle.vue'
import SmrInput from '@/components/form/SmrInput.vue'
import Icon from '@/components/Icon.vue'
import IconClose from '@/components/icons/IconClose.vue'
import TransportModeIcon from '@/components/TransportModeIcon.vue'
import IconOEAMTC from '@/components/icons/IconOEAMTC.vue'
import IconAlert from '@/components/icons/IconAlert.vue'
import { getRouteById, getDetailedRouteData, getRouteCountryInfo, getDetailedSectionsData } from '@/utils/route-utils'
import { formatDateFromTimestamp } from '@/utils/format'
import transportModes from '@/data/transportModes';
import POIList from '@/components/POIList.vue'
import { getCurrentSorting, getPOIResultByType } from '@/utils/poi-utils'
import { getPoiConfig } from '@/utils/poi-data/poi-config'
import { getPoiParams } from '@/utils/poi-params';
import { getAvoidLabel, getOtherSettingsLabel, getCountryName } from '@/utils/route-settings-labels'
import { PUBLIC_MEANS_OF_TRANSPORT } from '@data/publicMeansOfTransport';
import { HAS_PUBLIC_TRANSPORT } from '@/data/transportModesTypes';
import { loadMorePOIResultsWithPagination } from '@/utils/poi-api-utils'


export default {
    components: {
        PublicRouteOverview: () => import(/* webpackChunkName: "PublicRouteOverview" */ '@/components/PublicRouteOverview.vue'),
        SmrButton,
        WaypointSummary,
        RouteSteps,
        SmrToggle,
        SmrInput,
        POIList,
        Icon,
        IconClose,
        TransportModeIcon,
        IconOEAMTC,
        IconAlert,
    },
    data() {
        return {
            publicPath: process.env.BASE_URL,
            dialogVisible: false,
            note: '',
            route: null,
            routeDetail: null,
            publicRouteData: false,
            mapImage: false,
            mapImageLoading: false,
            routePois: {},
            poiLoading: false,
            poisLoaded: false,
            sections: [
                {
                    type: 'summary',
                    title: 'Route',
                    visible: true,
                },
                {
                    type: 'steps',
                    sectionTitle: 'Routenbeschreibung',
                    toggleable: true,
                    visible: true,
                },
                {
                    type: 'countryInfo',
                    sectionTitle: 'Länderinfo',
                    toggleable: true,
                    visible: true,
                },
                {
                    type: 'map',
                    sectionTitle: 'Übersichtskarte',
                    toggleable: true,
                    visible: true,
                },
                {
                    type: 'pois',
                    sectionTitle: 'Orte entlang der Route',
                    toggleable: true,
                    visible: true,
                },
                {
                    type: 'hint',
                    sectionTitle: 'Hinweise',
                    visible: true,
                },
            ]
        }
    },
    computed: {
        ...mapGetters(['activeRouteId', 'routeWaypoints', 'transportModes', 'activeRouteSettings', 'routeSettingsChanged', 'poiResults', 'selectedPOIs', 'isSavedRoute', 'savedRouteInfo', 'activeRouteSaved']),
        availableSections() {
            let availableSections = [...this.sections]
            if(!this.hasRoutePois) {
                availableSections = availableSections.filter(section => section.type !== 'pois')
            }
            return availableSections
        },
        formattedRoute() {
            if(this.route && this.routeDetail) {
                const route = {}

                route.mode = this.route.mode
                route.date = formatDateFromTimestamp(this.route.metadata.time.departure.plannedTime || this.route.metadata.time.departure.realTime)
                route.time = this.routeDetail.departureArrival ?? false // for public transport
                route.departureTime = this.routeDetail.departureTime && !route.time ? this.routeDetail.departureTime : false
                route.distance = this.routeDetail.distance ?? false
                route.duration = this.routeDetail.duration
                route.options = this.getRouteSettingsInfo() 
                route.countryInfo = getRouteCountryInfo(this.route)
                route.costs = this.getRouteCostInfo()
                route.transitions = this.routeDetail.transitions ?? false

                route.steps = this.routeDetail.steps
                route.map = this.mapImage
                return route
            }

            return false
        },
        routeModeLabel() {
            const mode = transportModes.find(mode => mode.value === this.route.mode)
            return mode.label
        },
        currentSorting() {
            return getCurrentSorting();
        },
        hasRoutePois() {
            if(Object.keys(this.poiResults).length === 0) return false
            const allPois = Object.keys(this.poiResults).map((type) => { return this.poiResults[type] });
            return allPois.length > 0
        },
    },
    mounted() {
        this.addListeners()
    },
    beforeDestroy() {
        this.removeListeners()
    },
    methods: {
        closeClickHandler() {
            eventBus.$emit('itemClosed');
            this.closeDialog();
        },
        showPrintPreviewHandler(e) {
            const routeId = e
            this.route = getRouteById(routeId)
            this.routeDetail = getDetailedRouteData(this.route)
            if(HAS_PUBLIC_TRANSPORT.includes(this.route.mode) || this.route.mode === 'SHARING') {
                this.publicRouteData = getDetailedSectionsData(this.route)
            } else {
                this.publicRouteData = false
            }
            if(this.isSavedRoute) {
                if(this.activeRouteSaved && this.activeRouteSaved.note && this.activeRouteSaved.note !== '') {
                    this.note = this.activeRouteSaved.note
                } else if(this.savedRouteInfo && this.savedRouteInfo.note && this.savedRouteInfo.note !== '') {
                    this.note = this.savedRouteInfo.note
                } else {
                    this.note = ''
                }
            } else {
                this.note = ''
            }
            this.getMapImage()
            this.resetSections()
            this.poisLoaded = false
            this.routePois = {}
            if(this.selectedPOIs.length > 0) this.getAllRoutePOIs()
            this.showDialog()
        },
        sectionVisibilityChangeHandler(e, index) {
            this.sections[index].visible = e
        },
        noteChangeHandler(e) {
            this.note = e
        },
        printClickHandler() {
            window.print()
        },
        addListeners() {
            eventBus.$on('showPrintPreview', this.showPrintPreviewHandler);
        },
        removeListeners() {
            eventBus.$off('showPrintPreview', this.showPrintPreviewHandler);
        },
        closeDialog() {
            document.body.classList.remove('print-route')
            this.dialogVisible = false;
        },
        showDialog() {
            document.body.classList.add('print-route')
            this.dialogVisible = true;
        },
        resetSections() {
            this.sections.forEach(section => {
                section.visible = true
            })
        },
        getRouteSettingsInfo() {
            let infoEntries = []
            const defaultText = `Standardroute ${this.routeModeLabel}`
            const settings = this.activeRouteSettings[this.route.mode.toLowerCase()]
            let hasTravelMode = false
            if(!settings || !this.routeSettingsChanged) {
                if(this.route.mode === 'BIKE') return 'Radinfrastruktur bevorzugt nutzen'
                return defaultText
            }
            if(settings.travelMode) {
                hasTravelMode = true
                if(settings.travelMode === 'SHORTEST') {
                    infoEntries.push('Kürzeste Strecke')
                } else if(settings.travelMode === 'FASTEST') {
                    infoEntries.push('Schnellste Strecke')
                }
            }
            if(settings.meansOfTransport) {
                const uniqueMeansOfTransport = [...new Set(settings.meansOfTransport)]
                if(uniqueMeansOfTransport.length !== PUBLIC_MEANS_OF_TRANSPORT.length) {
                    infoEntries.push('Verkehrsmittelwahl eingegrenzt')
                }
            }
            if(settings.avoid) {
                for(const key in settings.avoid) {
                    const value = settings.avoid[key]
                    if(value) {
                        const label = getAvoidLabel(key)
                        if(label) infoEntries.push(label)
                    }
                }
            }
            if(settings.avoidTunnels && settings.avoidTunnels !== '') {
                if(settings.avoidTunnels === '0') {
                    infoEntries.push('Alle Tunnel vermeiden')
                } else {
                    infoEntries.push(`Tunnel länger als ${settings.avoidTunnels}m vermeiden`)
                }
            }
            if(settings.avoidCountries && settings.avoidCountries.length > 0) {
                infoEntries.push(`Länder vermeiden: ${settings.avoidCountries.map(country => getCountryName(country)).join(', ')}`)
            }
            if(settings.avoidBarriers) {
                settings.avoidBarriers.forEach(value => {
                    const label = getAvoidLabel(value)
                    if(label) infoEntries.push(label)
                })
            }
            if(settings.other) {
                for(const key in settings.other) {
                    const value = settings.other[key]
                    if(value) {
                        const label = getOtherSettingsLabel(key)
                        if(label) infoEntries.push(label)
                    }
                }
            }
            if(settings.maxChanges && settings.maxChanges !== '-1') {
                if(settings.maxChanges === 0) {
                    infoEntries.push(`Nur Direktverbindungen`)
                } else {
                    infoEntries.push(`Umstiege: max. ${settings.maxChanges}x`)
                }
            }
            if(settings.bikeParameters) {
                if(settings.bikeParameters.avoidInclines) {
                    infoEntries.push('Steile Anstiege vermeiden')
                }
                if(settings.bikeParameters.preferCyclingInfrastructure) {
                    infoEntries.push('Radinfrastruktur bevorzugt nutzen')
                }
            }
            if(hasTravelMode && infoEntries.length === 1)  infoEntries = [defaultText, ...infoEntries] // just travel mode selected, put default text as first entry
            if(infoEntries.length === 0) return defaultText
            return infoEntries.join(' / ')
        },
        getRouteCostInfo() {
            let costEntries = []
            if(this.routeDetail.gasPrice) {
                costEntries.push(`Sprit: ca. ${this.routeDetail.gasPrice}`)
            }
            if(this.routeDetail.tolls) {
                if(this.routeDetail.tolls.tollSumEuro) {
                    costEntries.push(`Maut: ${this.routeDetail.tolls.tollSumEuro}`)
                }
                if(this.routeDetail.tolls.vignette) {
                    costEntries.push(`Vignettenpflicht (${this.routeDetail.tolls.vignette.map(vignette => vignette.country).join(', ')})`)
                }
            }
            if(this.routeDetail.fare) {
                costEntries.push(`Erwachsene: ${this.routeDetail.fare}`)
            }
            if(costEntries.length === 0) return false
            return costEntries
        },
        async getMapImage() {
            if(this.mapImageLoading) return
            try {
                this.mapImageLoading = true
                const journey = JSON.parse(JSON.stringify(this.route))
                for(const i in journey.sections) {
                    if(journey.sections[i].polyline) delete journey.sections[i].polyline // remove polyline to slim down request
                    if(journey.sections[i].turns) delete journey.sections[i].turns // remove polyline to slim down request
                }
                const params = {
                    journey: journey
                }
                const response = await api.smr.staticMap(params)
                if(response) {
                    const imageUrl = window.URL.createObjectURL(new Blob([response.data]));
                    this.mapImage = imageUrl
                }
                this.mapImageLoading = false
            } catch(error) {
                console.error(error)
                this.mapImageLoading = false
            }
        },
        async getAllRoutePOIs() {
            this.poiLoading = true
            let promises = this.selectedPOIs.map(poi => {
                return this.loadPOIType(poi)
            });
            await Promise.all(promises);
            this.poiLoading = false
            this.poisLoaded = true
        },
        async loadPOIType(type) {
            return new Promise((resolve) => {
                const poiConfig = getPoiConfig(type)
                const apiType = poiConfig.apiType;
                const params = getPoiParams(type);

                // to ensure correct loading
                params.selectedPOIsForSorting = [...this.selectedPOIs]

                api.poi.getPOIsForListByType(apiType, {...params}) 
                    .then(async (response) => {
                        let data = getPOIResultByType(response, type);
                        if(!data) data = []
                        const moreData = await loadMorePOIResultsWithPagination(apiType, params, type, response, false)
                        if(data && moreData) { 
                            data = [...data, ...moreData]
                        }
                        if(!this.routePois[type]) this.$set(this.routePois, type, [])
                        this.$set(this.routePois, type, [...this.routePois[type], ...data])
                        resolve()
                    })
                    .catch((error) => {
                        console.error(error);
                        this.routePois[type] = []
                        if(error.response) {
                            this.$store.commit('setPoiApiErrorsByType', {type, data: error.response.data.errors})
                        }
                    })
            })
        }
    }
}
</script>

<style lang="scss">
.print-preview {

    &__button {
        @include mq($until: tablet) {
            margin-top: 24px;
        }
    }

    &__section {
        padding: 24px 30px 32px;

        @include mq($until: mobile-landscape) {
            padding: 20px 16px 24px;
        }

        &:empty {
            padding: 4px 0;
        }

        &--head {
            padding-bottom: 8px;

            @include mq($until: tablet) {
                flex-direction: column;
                justify-content: flex-start;
                align-items: flex-start;
            }
        }

        &--highlight {
            background: $color-black-003;
            padding-top: 10px;
            padding-bottom: 10px;
            border-top: 1px solid $color-black-005;
            border-bottom: 1px solid $color-black-005;
        }

        &__visual {
            text-align: center;
            margin: 24px 0;

            img {
                max-width: 100%;
            }
        }
    }

    &__icon-title {
        &__icon {
            margin-right: 10px;
            line-height: 0;

            &--yellow {
                @include yellow-icon-square;
                &:before {
                    content: none;
                }
            }
        }
    }

    &__waypoints {
        margin-top: 20px;
        margin-bottom: 24px;
    }

    &__summary {
        display: flex;
        flex-wrap: wrap;

        &__item {
            width: 15%;
            margin-bottom: 20px;
            margin-right: 20px;

            @include mq($until: tablet) {
                width: 25%;
            }

            @include mq($until: mobile-landscape) {
                width: 35%;
            }

            &--wide {
                width: 55%;

                @include mq($until: tablet) {
                    width: 80%;
                }

                @include mq($until: mobile-landscape) {
                    width: 100%;
                }
            }
        }
    }

    &__note {
        margin-top: 4px;
    }

    &__country-info {
        &:not(:last-child) {
            margin-bottom: 48px;
        }

        &__title {
            margin-bottom: 24px;

            img {
                width: 27px;
                margin-right: 16px;
            }
        }

        &__entry {
            &:not(:last-child) {
                margin-bottom: 24px;
            }

            &__title {
                margin-bottom: 16px !important;
            }

            &__section {
                &:not(:last-child) {
                    margin-bottom: 20px;
                }

                &__label {
                    margin-bottom: 4px !important;

                    &--space-top {
                        margin-top: 8px !important;
                    }
                }

                &__text {
                    &--space-top {
                        margin-top: 4px;
                    }

                    strong {
                        font-weight: $font-weight-medium !important;
                    }
                }
            }
        }
    }

    &__map {
        width: 100%;
    }

    &__routing-hints {
        padding-top: 20px;
        padding-bottom: 20px;
        margin-bottom: 20px;
        border-top: 1px solid $color-black-005;
        border-bottom: 1px solid $color-black-005;

        &__item {
            gap: 20px;

            &:not(:last-child) {
                margin-bottom: 12px;
            }

            &__content {
                max-width: 700px;
            }
        }
    }
}
</style>